import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import { 
  Settings, 
  Bell, 
  Globe, 
  Moon, 
  Lock,
  Shield,
  HelpCircle,
  FileText,
  LogOut,
  ChevronRight,
  Smartphone,
  Mail,
  Volume2
} from 'lucide-react';

const SettingsPage = () => {
  const { t, i18n } = useTranslation();
  const { isAuthenticated, logout } = useAuth();
  const { theme, toggleTheme, isDark } = useTheme();
  const navigate = useNavigate();
  const lang = i18n.language;

  const [settings, setSettings] = useState({
    notifications: true,
    emailNotifications: true,
    smsNotifications: false,
    soundEnabled: true,
    language: lang,
  });

  const handleToggle = (key) => {
    setSettings(prev => ({ ...prev, [key]: !prev[key] }));
    toast.success(lang === 'ar' ? 'تم حفظ الإعدادات' : 'Settings saved');
  };

  const handleDarkModeToggle = () => {
    toggleTheme();
    toast.success(
      lang === 'ar' 
        ? (!isDark ? 'تم تفعيل الوضع الداكن' : 'تم تفعيل الوضع الفاتح')
        : (!isDark ? 'Dark mode enabled' : 'Light mode enabled')
    );
  };

  const handleLanguageChange = (value) => {
    setSettings(prev => ({ ...prev, language: value }));
    i18n.changeLanguage(value);
    toast.success(lang === 'ar' ? 'تم تغيير اللغة' : 'Language changed');
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const menuItems = [
    { icon: Lock, label: lang === 'ar' ? 'تغيير كلمة المرور' : 'Change Password', to: '/change-password' },
    { icon: Shield, label: lang === 'ar' ? 'الخصوصية والأمان' : 'Privacy & Security', to: '/privacy' },
    { icon: HelpCircle, label: lang === 'ar' ? 'المساعدة والدعم' : 'Help & Support', to: '/help' },
    { icon: FileText, label: lang === 'ar' ? 'الشروط والأحكام' : 'Terms & Conditions', to: '/terms' },
  ];

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900" data-testid="settings-page">
      {/* Header */}
      <div className="bg-white dark:bg-slate-800 border-b dark:border-slate-700">
        <div className="container mx-auto px-4 py-6">
          <div className="flex items-center gap-3">
            <Settings className="h-6 w-6 text-slate-700 dark:text-slate-300" />
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
              {lang === 'ar' ? 'الإعدادات' : 'Settings'}
            </h1>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-6 max-w-2xl space-y-6">
        {/* Notifications */}
        <Card className="dark:bg-slate-800 border-0 shadow-sm dark:bg-slate-800">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Bell className="h-5 w-5" />
              {lang === 'ar' ? 'الإشعارات' : 'Notifications'}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>{lang === 'ar' ? 'إشعارات التطبيق' : 'Push Notifications'}</Label>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  {lang === 'ar' ? 'تلقي إشعارات المواعيد والتحديثات' : 'Receive appointment and update notifications'}
                </p>
              </div>
              <Switch
                checked={settings.notifications}
                onCheckedChange={() => handleToggle('notifications')}
                data-testid="notifications-toggle"
              />
            </div>
            <Separator />
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Mail className="h-4 w-4 text-slate-400" />
                <Label>{lang === 'ar' ? 'إشعارات البريد' : 'Email Notifications'}</Label>
              </div>
              <Switch
                checked={settings.emailNotifications}
                onCheckedChange={() => handleToggle('emailNotifications')}
              />
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Smartphone className="h-4 w-4 text-slate-400" />
                <Label>{lang === 'ar' ? 'إشعارات SMS' : 'SMS Notifications'}</Label>
              </div>
              <Switch
                checked={settings.smsNotifications}
                onCheckedChange={() => handleToggle('smsNotifications')}
              />
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Volume2 className="h-4 w-4 text-slate-400" />
                <Label>{lang === 'ar' ? 'الأصوات' : 'Sound'}</Label>
              </div>
              <Switch
                checked={settings.soundEnabled}
                onCheckedChange={() => handleToggle('soundEnabled')}
              />
            </div>
          </CardContent>
        </Card>

        {/* Appearance */}
        <Card className="dark:bg-slate-800 border-0 shadow-sm dark:bg-slate-800">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Moon className="h-5 w-5" />
              {lang === 'ar' ? 'المظهر' : 'Appearance'}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>{lang === 'ar' ? 'الوضع الداكن' : 'Dark Mode'}</Label>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  {lang === 'ar' ? 'تفعيل المظهر الداكن' : 'Enable dark theme'}
                </p>
              </div>
              <Switch
                checked={isDark}
                onCheckedChange={handleDarkModeToggle}
                data-testid="dark-mode-toggle"
              />
            </div>
            <Separator />
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Globe className="h-4 w-4 text-slate-400" />
                <Label>{lang === 'ar' ? 'اللغة' : 'Language'}</Label>
              </div>
              <Select value={settings.language} onValueChange={handleLanguageChange}>
                <SelectTrigger className="w-[140px]" data-testid="language-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="en">English</SelectItem>
                  <SelectItem value="ar">العربية</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Quick Links */}
        <Card className="dark:bg-slate-800 border-0 shadow-sm dark:bg-slate-800">
          <CardContent className="p-2">
            {menuItems.map((item, index) => (
              <button
                key={index}
                onClick={() => navigate(item.to)}
                className="w-full flex items-center justify-between p-3 rounded-lg hover:bg-slate-50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <item.icon className="h-5 w-5 text-slate-400" />
                  <span className="font-medium text-slate-700 dark:text-slate-300">{item.label}</span>
                </div>
                <ChevronRight className="h-5 w-5 text-slate-400" />
              </button>
            ))}
          </CardContent>
        </Card>

        {/* Logout */}
        {isAuthenticated && (
          <Button 
            variant="outline" 
            className="w-full justify-center text-red-600 hover:text-red-700 hover:bg-red-50"
            onClick={handleLogout}
            data-testid="logout-button"
          >
            <LogOut className="h-5 w-5 mr-2" />
            {t('common.logout')}
          </Button>
        )}

        {/* App Version */}
        <p className="text-center text-sm text-slate-400">
          {lang === 'ar' ? 'الإصدار' : 'Version'} 1.0.0
        </p>
      </div>
    </div>
  );
};

export default SettingsPage;
